/**
 * searchableName.js
 *
 * Fonte única da normalização de `dadosPessoais.searchableName`.
 *
 * ── O contrato ────────────────────────────────────────────────────────────────
 * `searchableName` é o nome em MINÚSCULAS e SEM ACENTO. Não é campo de exibição:
 * existe só para as consultas de prefixo (`>= termo`, `<= termo + ''`), que
 * o Firestore resolve por ordem de code point — e nessa ordem `'ã'` (U+00E3) fica
 * fora de qualquer range que comece por `'a'` (U+0061).
 *
 * Por isso o termo digitado e o campo gravado precisam passar pela MESMA
 * normalização. Enquanto não passavam, quem tinha acento no nome ficava
 * inalcançável: `buscarPorNome` tirava o acento do termo e os pontos de escrita
 * gravavam com acento (só `toLowerCase()`), então nenhuma grafia encontrava a
 * pessoa — nem com acento, nem sem.
 *
 * ── Cuidado ao ampliar ────────────────────────────────────────────────────────
 * `AutoCompletePlus` atende mais de uma coleção, e normalizar o termo só é
 * correto onde o dado também está normalizado — daí a lista explícita
 * `COLECOES_SEARCHABLENAME_NORMALIZADO` em vez de normalizar sempre. Ao incluir
 * uma coleção nova aqui, os dois lados têm de andar juntos: os pontos de escrita
 * daquela coleção passam a usar `normalizarSearchableName` E o que já está
 * gravado precisa de backfill. Foi assim com `praticantes` e depois `entidades`.
 *
 * Em `entidades` há ainda um detalhe que não pode ser esquecido: além da busca,
 * `Entidades.Service.isNameRegistered` compara `searchableName` por IGUALDADE
 * para barrar nome duplicado. Normalizar só o dado deixaria essa checagem cega
 * para nomes com acento — e ela passaria a aceitar duplicatas.
 *
 * @module utils/searchableName
 */

import { removeAccents } from './removeAccents';

/**
 * Coleções cujo `dadosPessoais.searchableName` segue este contrato (gravado sem
 * acento). Só nelas o termo de busca deve ser normalizado.
 *
 * As coleções-espelho `praticantes.view` e `associacoes.view` ficaram de fora de
 * propósito: elas carregam o mesmo campo, mas ninguém as consulta por ele — são
 * usadas em listagens e sincronismo. Se algum dia virarem alvo de busca, entram
 * aqui junto com um backfill próprio.
 */
export const COLECOES_SEARCHABLENAME_NORMALIZADO = new Set(['praticantes', 'users', 'entidades']);

/**
 * Valor a gravar em `dadosPessoais.searchableName` a partir do nome.
 *
 * Colapsar espaços internos entra aqui porque alguns pontos de escrita já
 * faziam isso por conta própria (a importação de praticantes) e outros não —
 * duas grafias do mesmo nome geravam chaves diferentes.
 *
 * @param {string} nome nome como será exibido
 * @returns {string} minúsculas, sem acento, sem espaço nas pontas nem duplicado
 */
export const normalizarSearchableName = (nome) =>
  removeAccents(String(nome || '').trim().toLowerCase()).replace(/\s+/g, ' ');

/**
 * Normaliza o termo digitado para comparar com `searchableName`.
 *
 * @param {string} termo      texto digitado
 * @param {string} [colecao]  coleção consultada; fora de
 *   `COLECOES_SEARCHABLENAME_NORMALIZADO` o termo volta apenas em minúsculas,
 *   porque o dado daquela coleção ainda guarda acento.
 * @returns {string}
 */
export const normalizarTermoBusca = (termo, colecao) => {
  const base = String(termo || '').trim().toLowerCase();
  return COLECOES_SEARCHABLENAME_NORMALIZADO.has(colecao)
    ? normalizarSearchableName(base)
    : base;
};

export default normalizarSearchableName;
